import { type PartialUserContext, type UserContext } from '@common/defaults/UserContext';

/**
 * @name sanitizeSuperTokensData
 * @note getProps (or prepareProps) has already run at this point
 * so we can safely remove sensitive data from the userContext
 * before passing it to the client
 * @see https://pxr-tech.atlassian.net/browse/PB-7162
 */
export function sanitizeUserContext(userContext: UserContext): PartialUserContext {
    if (userContext.supertokens) {
        delete userContext.supertokens.sAccessToken;
        delete userContext.supertokens.sFrontToken;
        delete userContext.supertokens.sAntiCsrf;
        delete userContext.supertokens['st-last-access-token-update'];
    }

    return userContext;
}

/**
 * @description User subscription status based on allowAds, allowPremium and premiumSubscriptionTag
 */
export enum UserSubscriptionStatus {
    NOT_SUBSCRIBED = 'not-subscribed',
    TAG_BASED = 'tag-based',
    NO_ADS = 'no-ads',
    FULL_PREMIUM = 'full-premium',
}

/**
 * @name getUserSubscriptionStatus
 * @description Determines the user subscription status based on allowAds, allowPremium and
 * premiumSubscriptionTag.
 *  This is added to the UserContext to allow for a simpler check in the client-side code in e.g. the Paywall
 */
export const getUserSubscriptionStatus = ({
    isLoggedIn,
    allowAds,
    allowPremium,
    premiumSubscriptionTag,
}: {
    isLoggedIn: boolean;
    allowAds?: boolean | 'preview';
    allowPremium: boolean;
    premiumSubscriptionTag?: number;
}): UserSubscriptionStatus => {
    // Display paywall to logged out users or when allowAds is not explicitly set to false
    if (!isLoggedIn) {
        return UserSubscriptionStatus.NOT_SUBSCRIBED;
    }
    // Tag-based paywall e.g. club-subs
    if (allowAds === false && !!premiumSubscriptionTag) {
        return UserSubscriptionStatus.TAG_BASED;
    }
    // No-ads subscriptions if explicitly false (not 'preview' or undefined)
    if (allowAds === false && !allowPremium) {
        return UserSubscriptionStatus.NO_ADS;
    }
    // Full premium subscription: not TAG_BASED and not NO_ADS
    if (allowPremium) {
        return UserSubscriptionStatus.FULL_PREMIUM;
    }

    return UserSubscriptionStatus.NOT_SUBSCRIBED;
};
